/**
 * FauxDS Popover - Recreated from Figma MCP
 *
 * ⚠️ This is a SIMPLIFIED RECREATION of the Slate Popover component for prototyping.
 * It is NOT the real @faire/slate Popover and should NOT be used for:
 * - Engineering handoff
 * - Design specs
 * - Production code
 *
 * For the real Slate Popover, see: @faire/slate/components/popover
 *
 * This component was recreated from Figma MCP design specs:
 * - Figma node: 50140:9254 (Slate Popover)
 * - Design tokens extracted from Figma variables
 * - All variants verified: Position (Top, Bottom, Left, Right), Alignment (Start, Middle, End)
 * - Open/Closed states verified
 * - Footer variant verified
 */
import { cn } from "@faire/prototype-playground/lib/utils";
import React, { forwardRef, useRef, useEffect, useState, useId } from "react";
import { createPortal } from "react-dom";

export type PopoverPosition = "top" | "bottom" | "left" | "right";
export type PopoverAlignment = "start" | "middle" | "end";
export type PopoverFooterVariant = "action" | "customSlot";

export interface PopoverProps {
  /**
   * Whether the popover is open
   * @default true
   */
  open?: boolean;
  /**
   * Position of the popover relative to trigger
   * @default "top"
   */
  position?: PopoverPosition;
  /**
   * Alignment of the popover relative to trigger
   * @default "start"
   */
  alignment?: PopoverAlignment;
  /**
   * Title text. If omitted or empty, the title is not rendered.
   */
  title?: string;
  /**
   * Description text
   * @default "This example description spans three lines long, which is the recommended max length."
   */
  description?: string;
  /**
   * Whether to show footer variant (with action button)
   * @default "action"
   */
  footerVariant?: PopoverFooterVariant;
  /**
   * The props for the action button.
   *
   * Available only when `footerVariant` is `"action"`.
   */
  actionButtonProps?: {
    label: string;
    onClick?: () => void;
  };
  /**
   * Called when the popover should close
   */
  onClose?: () => void;
  /**
   * **playground only**
   * Optional className for additional styling
   */
  className?: string;
  /**
   * Ref to the trigger element (for positioning)
   */
  triggerRef?: React.RefObject<HTMLElement | null>;
  /**
   * Whether to show a directional arrow pointing toward the trigger
   * @default false
   */
  showArrow?: boolean;
}

// Close icon (X)
const CloseIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 4L12 12M12 4L4 12"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Popover - FauxDS (recreated from Figma MCP)
 *
 * Popovers provide users with additional, persistent information. They are intended
 * to be short sections of text or limited interactive content that pop up when a
 * user clicks on a particular UI element.
 *
 * Usage tips:
 * - Avoid using Popovers on Mobile Web or Native
 * - Use a Modal or Sheet component instead
 * - Use 4px spacing between a Popover and its trigger (baked into the component)
 * - Limit your usage of Popovers. Only use if you believe it is absolutely necessary
 *
 * This implementation was created by extracting design specs directly from Figma.
 */
const Popover = forwardRef<HTMLDivElement, PopoverProps>(
  (
    {
      open = true,
      position = "Top",
      alignment = "Start",
      title,
      description = "This example description spans three lines long, which is the recommended max length.",
      footerVariant = "action",
      actionButtonProps,
      onClose,
      className = "",
      triggerRef,
      showArrow = false,
    },
    ref
  ) => {
    const popoverRef = useRef<HTMLDivElement>(null);
    const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
    const id = useId();

    // Close on escape key
    useEffect(() => {
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === "Escape" && open) {
          onClose?.();
        }
      };

      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }, [open, onClose]);

    // Calculate position relative to trigger element
    useEffect(() => {
      if (open && popoverRef.current) {
        const popoverRect = popoverRef.current.getBoundingClientRect();
        const gap = 4; // 4px spacing from trigger (from Figma specs)

        let top = 0;
        let left = 0;

        // If triggerRef is provided, position relative to it
        if (triggerRef?.current) {
          const triggerRect = triggerRef.current.getBoundingClientRect();

          // Calculate position based on position prop
          switch (position) {
            case "top":
              top = triggerRect.top - popoverRect.height - gap;
              break;
            case "bottom":
              top = triggerRect.bottom + gap;
              break;
            case "left":
              left = triggerRect.left - popoverRect.width - gap;
              top =
                triggerRect.top + (triggerRect.height - popoverRect.height) / 2;
              break;
            case "right":
              left = triggerRect.right + gap;
              top =
                triggerRect.top + (triggerRect.height - popoverRect.height) / 2;
              break;
          }

          // Calculate horizontal alignment for top/bottom positions
          if (position === "top" || position === "bottom") {
            switch (alignment) {
              case "start":
                left = triggerRect.left;
                break;
              case "middle":
                left =
                  triggerRect.left +
                  (triggerRect.width - popoverRect.width) / 2;
                break;
              case "end":
                left = triggerRect.right - popoverRect.width;
                break;
            }
          }

          // Calculate vertical alignment for left/right positions
          if (position === "left" || position === "right") {
            switch (alignment) {
              case "start":
                top = triggerRect.top;
                break;
              case "middle":
                top =
                  triggerRect.top +
                  (triggerRect.height - popoverRect.height) / 2;
                break;
              case "end":
                top = triggerRect.bottom - popoverRect.height;
                break;
            }
          }
        } else {
          // Fallback: position in center of viewport (for demo purposes)
          top = window.innerHeight / 2 - popoverRect.height / 2;
          left = window.innerWidth / 2 - popoverRect.width / 2;
        }

        // Keep within viewport
        const padding = 16;
        left = Math.max(
          padding,
          Math.min(left, window.innerWidth - popoverRect.width - padding)
        );
        top = Math.max(
          padding,
          Math.min(top, window.innerHeight - popoverRect.height - padding)
        );

        setPopoverPosition({ top, left });
      }
    }, [open, position, alignment, triggerRef]);

    if (!open) {
      return null;
    }

    return createPortal(
      <div
        ref={ref || popoverRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={id}
        aria-describedby={`${id}-description`}
        className={cn(
          "fixed z-50 w-[280px] rounded-[var(--slate-radius-component-default)] bg-[var(--slate-color-action-surface-default)] p-[16px] shadow-lg",
          className
        )}
        style={{
          top: popoverPosition.top,
          left: popoverPosition.left,
        }}
      >
        {/* Arrow pointing toward trigger — positioned by alignment (start/middle/end), matching Slate behavior */}
        {showArrow && (position === "bottom" || position === "top") && (
          <div
            style={{
              position: "absolute",
              ...(position === "bottom" ? { top: -6 } : { bottom: -6 }),
              ...(alignment === "start"
                ? { left: 16 }
                : alignment === "end"
                  ? { right: 16 }
                  : { left: "50%", transform: "translateX(-50%)" }),
              width: 0,
              height: 0,
              borderLeft: "6px solid transparent",
              borderRight: "6px solid transparent",
              ...(position === "bottom"
                ? {
                    borderBottom: `6px solid var(--slate-color-action-surface-default)`,
                  }
                : {
                    borderTop: `6px solid var(--slate-color-action-surface-default)`,
                  }),
            }}
          />
        )}
        {/* Header with close button */}
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex-1">
            {/* Title */}
            {title ? (
              <p
                id={id}
                className="mb-2 text-[var(--slate-color-action-text-inverse)]"
                style={{
                  fontFamily: "var(--slate-typography-family-sans-serif)",
                  fontSize: "var(--slate-typography-font-size-200)",
                  fontWeight: "var(--slate-typography-weight-medium)",
                  lineHeight: "var(--slate-typography-line-height-200)",
                  letterSpacing: "var(--slate-typography-letter-spacing-200)",
                }}
              >
                {title}
              </p>
            ) : null}
            {/* Description */}
            <p
              id={`${id}-description`}
              className="text-[var(--slate-color-action-text-inverse)]"
              style={{
                fontFamily: "var(--slate-typography-family-sans-serif)",
                fontSize: "var(--slate-typography-font-size-200)",
                fontWeight: "var(--slate-typography-weight-regular)",
                lineHeight: "var(--slate-typography-line-height-200)",
                letterSpacing: "var(--slate-typography-letter-spacing-200)",
              }}
            >
              {description}
            </p>
          </div>
          {/* Close button */}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className={cn(
                "-m-1 shrink-0 cursor-pointer rounded-sm p-1 text-[var(--slate-color-action-icon-inverse)] hover:opacity-80 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-[var(--slate-color-action-border-focus-ring)] focus-visible:ring-offset-2"
              )}
              aria-label="Close popover"
            >
              <CloseIcon />
            </button>
          )}
        </div>

        {/* Footer action button */}
        {footerVariant === "action" && actionButtonProps?.label && (
          <button
            type="button"
            onClick={actionButtonProps.onClick}
            className={cn(
              "cursor-pointer rounded-sm text-[var(--slate-color-action-text-inverse)] underline underline-offset-2 hover:opacity-80 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-[var(--slate-color-action-border-focus-ring)] focus-visible:ring-offset-2"
            )}
            style={{
              fontFamily: "var(--slate-typography-family-sans-serif)",
              fontSize: "var(--slate-typography-font-size-200)",
              fontWeight: "var(--slate-typography-weight-regular)",
              lineHeight: "var(--slate-typography-line-height-200)",
              letterSpacing: "var(--slate-typography-letter-spacing-200)",
            }}
          >
            {actionButtonProps.label}
          </button>
        )}
      </div>,
      document.body
    );
  }
);

Popover.displayName = "Popover";

export default Popover;
